// AccommodationAnimalsBlock — "RRA or pending RRA" on the Animal Addendum Send page (WEL-182).
//
// Staff-only. Each row is an animal the household will see listed at $0 on its form and on the
// addendum: name, kind, an optional breed (dogs — the only kind the household form asks it of),
// and a choice, RRA (approved) or Pending RRA. A pending row also carries what applies if the
// request is not approved, defaulting to the page's per-new-animal rent and deposit.
//
// The block is controlled and logic-free: every rule (validation, defaults, the wire shape) is in
// lib/animalAddendumAccommodation.ts. It says "RRA" because the staff page may; the household form
// never does (Rule H).
//
//   cd apps/web && NODE_ENV=test npx tsx --experimental-test-module-mocks --test src/components/AccommodationAnimalsBlock.test.ts

import { useId } from 'react';
import { MoneyInput } from '@/components/MoneyInput';
import {
  ACCOMMODATION_COPY as COPY,
  MAX_ACCOMMODATION_BREED,
  MAX_ACCOMMODATION_NAME,
  accommodationAsksBreed,
  canAddAccommodationRow,
  effectiveIfNotApprovedDepositCents,
  effectiveIfNotApprovedRentCents,
  type AccommodationDecision,
  type AccommodationRates,
  type AccommodationRow,
} from '@/lib/animalAddendumAccommodation';
import { ANIMAL_KIND_CHOICES, type AnimalKindChoice } from '@/lib/animalQuestions';

const DECISIONS: readonly AccommodationDecision[] = ['approved', 'pending'];

export function AccommodationAnimalsBlock(props: {
  rows: ReadonlyArray<AccommodationRow>;
  /** This page's per-new-animal amounts — what a pending row defaults to. */
  rates: AccommodationRates;
  disabled?: boolean;
  onAdd: () => void;
  onUpdate: (key: string, patch: Partial<Omit<AccommodationRow, 'key'>>) => void;
  onRemove: (key: string) => void;
}): React.JSX.Element {
  const { rows, rates } = props;
  const disabled = props.disabled === true;
  const uid = useId();

  return (
    <div
      data-section="accommodation-animals"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
        paddingTop: 'var(--space-4)',
        borderTop: '1px solid var(--border)',
      }}
    >
      <div>
        <div style={{ fontWeight: 'var(--weight-medium)', color: 'var(--fg)' }}>{COPY.title}</div>
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', margin: 'var(--space-1) 0 0' }}>
          {COPY.helper}
        </p>
      </div>

      {rows.map((row, idx) => {
        const base = `${uid}-${idx}`;
        const pending = row.decision === 'pending';
        return (
          <div
            key={row.key}
            data-row="accommodation-animal"
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-3)',
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--space-3)', alignItems: 'end' }}>
              <div className="field">
                <label htmlFor={`${base}-name`}>{COPY.nameLabel}</label>
                <input
                  id={`${base}-name`}
                  type="text"
                  className="input"
                  maxLength={MAX_ACCOMMODATION_NAME}
                  value={row.name}
                  disabled={disabled}
                  onChange={(e) => props.onUpdate(row.key, { name: e.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor={`${base}-kind`}>{COPY.kindLabel}</label>
                <select
                  id={`${base}-kind`}
                  className="input"
                  value={row.kind}
                  disabled={disabled}
                  onChange={(e) => props.onUpdate(row.key, { kind: e.target.value as AnimalKindChoice | '' })}
                >
                  <option value="">{COPY.kindPlaceholder}</option>
                  {ANIMAL_KIND_CHOICES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              {accommodationAsksBreed(row) ? (
                <div className="field">
                  <label htmlFor={`${base}-breed`}>{COPY.breedLabel}</label>
                  <input
                    id={`${base}-breed`}
                    type="text"
                    className="input"
                    maxLength={MAX_ACCOMMODATION_BREED}
                    value={row.breed}
                    disabled={disabled}
                    onChange={(e) => props.onUpdate(row.key, { breed: e.target.value })}
                  />
                </div>
              ) : null}
            </div>

            <fieldset className="fieldset" style={{ border: 0, padding: 0, marginInline: 0, minWidth: 0 }}>
              <legend style={{ fontSize: 'var(--text-sm)', padding: 0 }}>{COPY.decisionLegend}</legend>
              <div className="cluster" style={{ gap: 'var(--space-5)' }}>
                {DECISIONS.map((d) => (
                  <label key={d} className="choice">
                    <input
                      type="radio"
                      name={`${base}-decision`}
                      value={d}
                      checked={row.decision === d}
                      disabled={disabled}
                      onChange={() => props.onUpdate(row.key, { decision: d })}
                    />
                    <span>{d === 'approved' ? COPY.approved : COPY.pending}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            {pending ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--space-3)' }}>
                <div className="field">
                  <label htmlFor={`${base}-rent`}>{COPY.ifNotApprovedRent}</label>
                  <MoneyInput
                    id={`${base}-rent`}
                    valueCents={effectiveIfNotApprovedRentCents(row, rates)}
                    disabled={disabled}
                    onChangeCents={(cents) => props.onUpdate(row.key, { ifNotApprovedRentCents: cents })}
                  />
                  <span className="helper">{COPY.ifNotApprovedHelper}</span>
                </div>
                <div className="field">
                  <label htmlFor={`${base}-dep`}>{COPY.ifNotApprovedDeposit}</label>
                  <MoneyInput
                    id={`${base}-dep`}
                    valueCents={effectiveIfNotApprovedDepositCents(row, rates)}
                    disabled={disabled}
                    onChangeCents={(cents) => props.onUpdate(row.key, { ifNotApprovedDepositCents: cents })}
                  />
                </div>
              </div>
            ) : null}

            <div>
              <button
                type="button"
                className="btn"
                data-variant="ghost"
                data-size="sm"
                disabled={disabled}
                aria-label={`${COPY.remove} ${row.name.trim() || `animal ${idx + 1}`}`}
                onClick={() => props.onRemove(row.key)}
              >
                {COPY.remove}
              </button>
            </div>
          </div>
        );
      })}

      <div>
        <button
          type="button"
          className="btn"
          data-variant="secondary"
          data-size="sm"
          disabled={disabled || !canAddAccommodationRow(rows)}
          onClick={props.onAdd}
        >
          + {COPY.add}
        </button>
        {!canAddAccommodationRow(rows) ? (
          <span className="helper" style={{ marginLeft: 'var(--space-3)' }}>
            {COPY.addFull}
          </span>
        ) : null}
      </div>
    </div>
  );
}
